import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isAuthenticated } from '@core/auth/kc-tokens';
import { ROUTES } from '../../../../app.routes';
import { PpcButton } from '@design-system/button/button';
import { PpcEyebrow } from '@design-system/eyebrow/eyebrow';

@Component({
  selector: 'app-nav',
  imports: [RouterLink, TranslatePipe, PpcButton, PpcEyebrow],
  templateUrl: './nav.html',
  styleUrl: './nav.css',
})
export class NavComponent {

  protected readonly scrolled = signal(false);
  protected readonly menuOpen = signal(false);
  private readonly translate = inject(TranslateService);

  protected readonly langs = this.translate.getLangs();
  protected readonly currentLang = this.translate.currentLang;

  protected readonly isAuthenticated = isAuthenticated;
  protected readonly routes = ROUTES;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly LANG_STORAGE_KEY = 'ppc.lang';

  protected readonly langOptions = computed(() =>
    this.langs.map((code) => ({ code, label: code.toUpperCase() })),
  );

  protected readonly accountLabelKey = computed(() =>
    this.isAuthenticated() ? 'nav.manage' : 'nav.login',
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = '';
    });
  }

  @HostListener('window:scroll')
  onScroll() {
    this.scrolled.set(window.scrollY > 40);
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.menuOpen()) this.closeMenu();
  }

  protected toggleMenu() {
    const next = !this.menuOpen();
    this.menuOpen.set(next);
    document.body.style.overflow = next ? 'hidden' : '';
  }

  protected closeMenu() {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    document.body.style.overflow = '';
  }

  protected onAccount() {
    this.closeMenu();
    // A member renews from the account page, never straight from the landing; a guest logs in first.
    void this.router.navigate([this.isAuthenticated() ? ROUTES.profile : ROUTES.login]);
  }

  protected switchLang(code: string) {
    if (code === this.currentLang()) return;
    localStorage.setItem(this.LANG_STORAGE_KEY, code);
    document.documentElement.lang = code;
    this.translate.use(code).subscribe();
  }
}
